import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import { getFavouriteListingIds, isListingFavourite } from '../../../util/favourites';
import { toggleFavouriteListing } from '../../../ducks/favourites.duck';

import css from './SearchResultsPanel.module.css';

const FEATURED_LISTING_TITLE = 'Soft Glam Makeup Artist';
const FEATURED_ARTIST_NAME = 'Chloe Rophael';
const NADINE_ARTIST_NAME = 'Beauty by Nadine Marie';
const GLORIA_ARTIST_NAME = 'Gloria Dawood';

const getArtistName = listing =>
  listing?.author?.attributes?.profile?.displayName?.trim().toLowerCase() || '';

const isFeaturedListing = listing => {
  const title = listing?.attributes?.title?.trim().toLowerCase();
  const artistName = getArtistName(listing);

  return (
    title === FEATURED_LISTING_TITLE.toLowerCase() &&
    artistName === FEATURED_ARTIST_NAME.toLowerCase()
  );
};

const swapNadineAndGloria = listings => {
  const reorderedListings = [...listings];
  const nadineIndex = reorderedListings.findIndex(
    listing => getArtistName(listing) === NADINE_ARTIST_NAME.toLowerCase()
  );
  const gloriaIndex = reorderedListings.findIndex(
    listing => getArtistName(listing) === GLORIA_ARTIST_NAME.toLowerCase()
  );

  if (nadineIndex === -1 || gloriaIndex === -1) {
    return reorderedListings;
  }

  [reorderedListings[nadineIndex], reorderedListings[gloriaIndex]] = [
    reorderedListings[gloriaIndex],
    reorderedListings[nadineIndex],
  ];

  return reorderedListings;
};

const HeartIcon = ({ filled }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 20.25c-.26 0-.51-.09-.71-.27C7.19 16.36 4.5 13.91 4.5 10.5A4.25 4.25 0 0 1 8.75 6.25c1.31 0 2.56.6 3.25 1.55a4.04 4.04 0 0 1 3.25-1.55A4.25 4.25 0 0 1 19.5 10.5c0 3.41-2.69 5.86-6.79 9.48-.2.18-.45.27-.71.27Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchResultsPanel = props => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    favouriteListingIds: favouriteListingIdsProp,
    onToggleFavourite: onToggleFavouriteProp,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';
  const favouriteListingIds = favouriteListingIdsProp || getFavouriteListingIds(currentUser);

  const onToggleFavourite =
    onToggleFavouriteProp ||
    (listingId => {
      if (!currentUser) {
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
        return Promise.resolve();
      }

      return dispatch(toggleFavouriteListing(listingId)).catch(error => {
        console.error('Unable to update favourite listing', error);
      });
    });

  const featuredFirstListings = [...listings].sort((a, b) => {
    return Number(isFeaturedListing(b)) - Number(isFeaturedListing(a));
  });
  const sortedListings = swapNadineAndGloria(featuredFirstListings);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      return '(max-width: 1023px) 132px, 152px';
    }

    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      '(max-width: 1023px) 33vw',
      '(max-width: 1279px) 25vw',
      '20vw',
    ].join(', ');
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {sortedListings.map(l => {
          const listingId = l.id.uuid;
          const title = l?.attributes?.title || 'listing';
          const isFavourite = isListingFavourite(favouriteListingIds, listingId);
          const favouriteLabel = isFavourite
            ? `Remove ${title} from favourites`
            : `Save ${title} to favourites`;

          return (
            <li key={listingId} className={css.resultItem}>
              <div className={css.cardShell}>
                <ListingCard
                  className={css.listingCard}
                  listing={l}
                  renderSizes={cardRenderSizes(isMapVariant)}
                  setActiveListing={setActiveListing}
                  showLocationInfo
                  horizontal={isMapVariant}
                  intl={intl}
                />
                {!isMapVariant ? (
                  <button
                    type="button"
                    className={classNames(css.favouriteButton, {
                      [css.favouriteButtonActive]: isFavourite,
                    })}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleFavourite(listingId);
                    }}
                    aria-label={favouriteLabel}
                    aria-pressed={isFavourite}
                  >
                    <HeartIcon filled={isFavourite} />
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
